import { useState, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
    ArrowLeft, Download, Upload, FileJson, FileSpreadsheet,
    Loader2, Check, X, AlertTriangle,
    Workflow, Trash2, ChevronDown, ChevronUp, Globe,
    UserPlus, Tag, Zap, Bell, Timer,
    Plus, Pencil, FolderOpen, ImagePlus,
} from 'lucide-react';
import {
    getPushDomains, createPushFlow, createPushFlowNode, getPushGroups,
} from '@/services/api/push';
import { getMedia, uploadMedia, getMediaUrl, getThumbnailUrl } from '@/services/api';
import type { MediaItem } from '@/services/api/media';
import type { PushFlowCreate, PushFlowNodeCreate } from '@/types/push';

interface StepItem {
    type: 'notification' | 'delay';
    // notification
    title?: string;
    body?: string;
    url?: string;
    icon?: string;
    image?: string;
    badge?: string;
    utm_campaign?: string;
    tag_on_receive?: string;
    tag_on_click?: string;
    // delay
    delay_minutes?: number;
}

type BatchFlowItem = PushFlowCreate & {
    steps?: StepItem[];
    _valid?: boolean;
    _errors?: string[];
};

const VALID_STEP_TYPES = ['notification', 'delay'] as const;

const TEMPLATE_FIELDS = [
    { key: 'name', label: 'Nome do Flow', required: true, example: 'Welcome Flow' },
    { key: 'description', label: 'Descrição', required: false, example: 'Flow de boas-vindas para novos leads' },
    { key: 'trigger_type', label: 'Trigger (new_lead/tag_added)', required: true, example: 'new_lead' },
    { key: 'trigger_tag', label: 'Tag do trigger', required: false, example: '' },
    { key: 'utm_source', label: 'UTM Source', required: false, example: 'push_notification' },
    { key: 'utm_medium', label: 'UTM Medium', required: false, example: 'push' },
    { key: 'steps', label: 'Steps do flow', required: false, example: '(array de steps)' },
];

function validateStep(step: any, index: number): string[] {
    const errors: string[] = [];
    const prefix = `Step ${index + 1}`;

    if (!step.type) {
        errors.push(`${prefix}: type é obrigatório`);
        return errors;
    }

    if (!VALID_STEP_TYPES.includes(step.type)) {
        errors.push(`${prefix}: type "${step.type}" inválido (use: ${VALID_STEP_TYPES.join(', ')})`);
        return errors;
    }

    switch (step.type) {
        case 'notification':
            if (!step.title) errors.push(`${prefix}: title é obrigatório para notification`);
            if (!step.body) errors.push(`${prefix}: body é obrigatório para notification`);
            if (!step.url) errors.push(`${prefix}: url é obrigatório para notification`);
            break;
        case 'delay':
            if (!step.delay_minutes || step.delay_minutes <= 0) errors.push(`${prefix}: delay_minutes deve ser > 0`);
            break;
    }

    return errors;
}

function validateFlow(f: any): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    if (!f.name) errors.push('Nome é obrigatório');
    if (!f.trigger_type) errors.push('Trigger type é obrigatório');
    if (!['new_lead', 'tag_added'].includes(f.trigger_type)) errors.push('Trigger type inválido');
    if (f.trigger_type === 'tag_added' && !f.trigger_tag) errors.push('Tag é obrigatória para trigger tag_added');

    if (f.steps && Array.isArray(f.steps)) {
        for (let i = 0; i < f.steps.length; i++) {
            errors.push(...validateStep(f.steps[i], i));
        }
    }

    return { valid: errors.length === 0, errors };
}

function formatDelayMinutes(minutes: number): string {
    if (minutes < 60) return `${minutes} min`;
    if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
    return `${Math.round(minutes / 1440)} dia${Math.round(minutes / 1440) !== 1 ? 's' : ''}`;
}

function getStepIcon(type: string) {
    switch (type) {
        case 'notification': return <Bell className="h-3.5 w-3.5 text-blue-600" />;
        case 'delay': return <Timer className="h-3.5 w-3.5 text-amber-600" />;
        default: return null;
    }
}

function getStepSummary(step: StepItem): string {
    switch (step.type) {
        case 'notification':
            return step.title || 'Notificação';
        case 'delay':
            return `Aguardar ${formatDelayMinutes(step.delay_minutes || 0)}`;
        default:
            return step.type;
    }
}

function getStepColor(type: string): string {
    switch (type) {
        case 'notification': return 'bg-blue-50 border-blue-200';
        case 'delay': return 'bg-amber-50 border-amber-200';
        default: return 'bg-gray-50 border-gray-200';
    }
}

export default function FlowBatch() {
    const [step, setStep] = useState<'upload' | 'preview'>('upload');
    const [destinationType, setDestinationType] = useState<'domain' | 'group'>('domain');
    const [selectedDomainId, setSelectedDomainId] = useState<number | null>(null);
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
    const [items, setItems] = useState<BatchFlowItem[]>([]);
    const [expandedIdx, setExpandedIdx] = useState<number | null>(0);
    const [editingStep, setEditingStep] = useState<{ flowIdx: number; stepIdx: number } | null>(null);
    const [mediaPicker, setMediaPicker] = useState<{ flowIdx: number; stepIdx: number; field: 'icon' | 'image' } | null>(null);
    const [uploading, setUploading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [progress, setProgress] = useState<{ flow: number; totalFlows: number; step: number; totalSteps: number } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { toast } = useToast();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { data: domains = [] } = useQuery({ queryKey: ['push-domains'], queryFn: getPushDomains });
    const configuredDomains = domains.filter((d) => d.push_config?.mongo_domain_id);

    const { data: groups = [] } = useQuery({ queryKey: ['push-groups'], queryFn: getPushGroups });
    const { data: mediaList = [], refetch: refetchMedia } = useQuery<MediaItem[]>({
        queryKey: ['media-list'],
        queryFn: () => getMedia(),
    });

    const downloadJsonTemplate = () => {
        const template = {
            name: 'Welcome Flow',
            description: 'Flow completo de boas-vindas para novos leads',
            trigger_type: 'new_lead',
            trigger_tag: '',
            utm_source: 'push_notification',
            utm_medium: 'push',
            steps: [
                {
                    type: 'notification',
                    title: 'Bem-vindo!',
                    body: 'Obrigado por se inscrever. Confira nosso conteúdo!',
                    url: 'https://example.com/welcome',
                    icon: '',
                    image: '',
                    utm_campaign: '',
                    tag_on_click: '',
                },
                {
                    type: 'delay',
                    delay_minutes: 30,
                },
                {
                    type: 'notification',
                    title: 'Você viu nosso conteúdo?',
                    body: 'Temos novidades que podem te interessar!',
                    url: 'https://example.com/news',
                },
                {
                    type: 'delay',
                    delay_minutes: 60,
                },
                {
                    type: 'notification',
                    title: 'Oferta especial para você',
                    body: 'Aproveite antes que acabe!',
                    url: 'https://example.com/offer',
                    tag_on_click: 'convertido',
                },
            ],
        };
        const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'flows_template.json'; a.click();
        URL.revokeObjectURL(url);
    };

    const downloadCsvTemplate = () => {
        const headers = ['name', 'description', 'trigger_type', 'trigger_tag', 'utm_source', 'utm_medium'].join(',');
        const row1 = ['"Welcome Flow"', '"Flow de boas-vindas"', '"new_lead"', '""', '"push_notification"', '"push"'].join(',');
        const row2 = ['"Re-engajamento Flow"', '"Reconquiste leads inativos"', '"tag_added"', '"inativo"', '"push_notification"', '"push"'].join(',');
        const csv = `${headers}\n${row1}\n${row2}`;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'flows_template.csv'; a.click();
        URL.revokeObjectURL(url);
    };

    const parseCsv = (text: string): any[] => {
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length < 2) return [];
        const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        return lines.slice(1).map(line => {
            const values: string[] = [];
            let current = '';
            let inQuotes = false;
            for (const char of line) {
                if (char === '"') { inQuotes = !inQuotes; continue; }
                if (char === ',' && !inQuotes) { values.push(current.trim()); current = ''; continue; }
                current += char;
            }
            values.push(current.trim());
            const obj: any = {};
            headers.forEach((h, i) => { obj[h] = values[i] || ''; });
            return obj;
        });
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                let data: any[];
                if (file.name.endsWith('.json')) {
                    data = JSON.parse(ev.target?.result as string);
                    if (!Array.isArray(data)) data = [data];
                } else if (file.name.endsWith('.csv')) {
                    data = parseCsv(ev.target?.result as string);
                } else {
                    toast({ variant: 'destructive', title: 'Formato não suportado', description: 'Use .json ou .csv' });
                    return;
                }

                const validated = data.map(item => {
                    const { valid, errors } = validateFlow(item);
                    return {
                        ...item,
                        ...(destinationType === 'domain' ? { domain_id: selectedDomainId! } : { group_id: selectedGroupId! }),
                        trigger_type: item.trigger_type || 'new_lead',
                        _valid: valid,
                        _errors: errors,
                    } as BatchFlowItem;
                });

                setItems(validated);
                setStep('preview');
                toast({ title: `${validated.length} flows carregados` });
            } catch (err: any) {
                toast({ variant: 'destructive', title: 'Erro ao ler arquivo', description: err.message });
            }
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    const removeItem = (idx: number) => {
        setItems(prev => prev.filter((_, i) => i !== idx));
    };

    const updateFlowField = useCallback((flowIdx: number, field: string, value: string) => {
        setItems(prev => prev.map((item, i) => {
            if (i !== flowIdx) return item;
            const updated = { ...item, [field]: value };
            const { valid, errors } = validateFlow(updated);
            return { ...updated, _valid: valid, _errors: errors };
        }));
    }, []);

    const updateStep = useCallback((flowIdx: number, stepIdx: number, field: string, value: string | number) => {
        setItems(prev => prev.map((item, i) => {
            if (i !== flowIdx) return item;
            const steps = [...(item.steps || [])];
            steps[stepIdx] = { ...steps[stepIdx], [field]: value };
            const updated = { ...item, steps };
            const { valid, errors } = validateFlow(updated);
            return { ...updated, _valid: valid, _errors: errors };
        }));
    }, []);

    const removeStep = useCallback((flowIdx: number, stepIdx: number) => {
        setItems(prev => prev.map((item, i) => {
            if (i !== flowIdx) return item;
            const steps = (item.steps || []).filter((_, si) => si !== stepIdx);
            const updated = { ...item, steps };
            const { valid, errors } = validateFlow(updated);
            return { ...updated, _valid: valid, _errors: errors };
        }));
        setEditingStep(null);
    }, []);

    const addStep = useCallback((flowIdx: number, type: StepItem['type']) => {
        setItems(prev => prev.map((item, i) => {
            if (i !== flowIdx) return item;
            const steps = [...(item.steps || [])];
            if (type === 'notification') {
                steps.push({ type: 'notification', title: '', body: '', url: '' });
            } else {
                steps.push({ type: 'delay', delay_minutes: 30 });
            }
            const updated = { ...item, steps };
            const { valid, errors } = validateFlow(updated);
            return { ...updated, _valid: valid, _errors: errors };
        }));
        // Open editing for the new step
        const item = items[flowIdx];
        const newIdx = (item.steps?.length || 0);
        setEditingStep({ flowIdx, stepIdx: newIdx });
    }, [items]);

    const handleMediaUpload = async (file: File) => {
        if (!mediaPicker) return;
        setUploading(true);
        try {
            const isIcon = mediaPicker.field === 'icon';
            const media = await uploadMedia(file, isIcon);
            updateStep(mediaPicker.flowIdx, mediaPicker.stepIdx, mediaPicker.field, getMediaUrl(media.filepath));
            refetchMedia();
            setMediaPicker(null);
            toast({ title: 'Upload concluído!' });
        } catch {
            toast({ variant: 'destructive', title: 'Erro no upload' });
        }
        setUploading(false);
    };

    const validItems = items.filter(i => i._valid);
    const invalidItems = items.filter(i => !i._valid);

    const totalStepsCount = validItems.reduce((acc, item) => acc + (item.steps?.length || 0), 0);

    const handleSubmit = async () => {
        setCreating(true);
        let created = 0;
        let failed = 0;

        for (let fi = 0; fi < validItems.length; fi++) {
            const item = validItems[fi];
            const { _valid, _errors, steps, ...data } = item;
            setProgress({ flow: fi + 1, totalFlows: validItems.length, step: 0, totalSteps: steps?.length || 0 });

            try {
                const createdFlow = await createPushFlow(data as PushFlowCreate);

                if (steps && steps.length > 0) {
                    for (let si = 0; si < steps.length; si++) {
                        setProgress({ flow: fi + 1, totalFlows: validItems.length, step: si + 1, totalSteps: steps.length });
                        const s = steps[si];
                        const nodeData: PushFlowNodeCreate = {
                            flow_id: createdFlow.id,
                            type: s.type,
                            position: si + 1,
                        };

                        if (s.type === 'notification') {
                            nodeData.title = s.title;
                            nodeData.body = s.body;
                            nodeData.url = s.url;
                            if (s.icon) nodeData.icon = s.icon;
                            if (s.image) nodeData.image = s.image;
                            if (s.badge) nodeData.badge = s.badge;
                            if (s.utm_campaign) nodeData.utm_campaign = s.utm_campaign;
                            if (s.tag_on_receive) nodeData.tag_on_receive = s.tag_on_receive;
                            if (s.tag_on_click) nodeData.tag_on_click = s.tag_on_click;
                        } else if (s.type === 'delay') {
                            nodeData.delay_minutes = s.delay_minutes;
                        }

                        await createPushFlowNode(nodeData);
                    }
                }

                created++;
            } catch {
                failed++;
            }
        }

        setCreating(false);
        setProgress(null);
        queryClient.invalidateQueries({ queryKey: ['push-flows'] });
        toast({
            title: `${created} flow${created !== 1 ? 's' : ''} criado${created !== 1 ? 's' : ''}${failed > 0 ? `, ${failed} falharam` : ''}`,
            variant: failed > 0 ? 'destructive' : undefined,
        });
        if (created > 0) navigate('/push/flows');
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" onClick={() => navigate('/push/flows')} className="gap-1.5 rounded-xl">
                    <ArrowLeft className="h-4 w-4" /> Voltar
                </Button>
                <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg shadow-emerald-200">
                        <Workflow className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">Criar Flows em Lote</h1>
                        <p className="mt-0.5 text-sm text-gray-500">Importe flows com steps via JSON ou CSV para criação em massa</p>
                    </div>
                </div>
            </div>

            {step === 'upload' && (
                <div className="space-y-6">
                    {/* Destination Selection: Domain or Group */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="flex items-center gap-2 mb-4">
                            <Globe className="h-5 w-5 text-gray-400" />
                            <h2 className="text-sm font-semibold text-gray-900">1. Escolha o destino</h2>
                        </div>
                        <p className="text-xs text-gray-500 mb-4">Selecione um domínio ou grupo de notificação</p>

                        {/* Tabs */}
                        <div className="flex gap-1 rounded-xl bg-gray-100 p-1 mb-4 w-fit">
                            <button
                                onClick={() => { setDestinationType('domain'); setSelectedGroupId(null); }}
                                className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-medium transition-all ${
                                    destinationType === 'domain' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <Globe className="h-3.5 w-3.5" /> Domínio
                            </button>
                            {groups.length > 0 && (
                                <button
                                    onClick={() => { setDestinationType('group'); setSelectedDomainId(null); }}
                                    className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-medium transition-all ${
                                        destinationType === 'group' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    <FolderOpen className="h-3.5 w-3.5" /> Grupo
                                </button>
                            )}
                        </div>

                        {/* Domain list */}
                        {destinationType === 'domain' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {configuredDomains.map((d) => {
                                    const isSelected = selectedDomainId === d.id;
                                    return (
                                        <button
                                            key={d.id}
                                            onClick={() => setSelectedDomainId(d.id)}
                                            className={`relative flex items-center gap-3 rounded-xl border-2 p-4 transition-all text-left ${
                                                isSelected ? 'border-emerald-500 bg-emerald-50/30 shadow-sm' : 'border-gray-100 hover:border-gray-300'
                                            }`}
                                        >
                                            {isSelected && <div className="absolute top-2.5 right-2.5 h-3 w-3 rounded-full bg-emerald-500" />}
                                            <img
                                                src={`https://www.google.com/s2/favicons?domain=${d.domain}&sz=64`}
                                                alt="" className="h-9 w-9 rounded-lg"
                                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                            />
                                            <div className="min-w-0">
                                                <p className={`text-sm font-semibold truncate ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>{d.domain}</p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Group list */}
                        {destinationType === 'group' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {groups.map((g) => {
                                    const isSelected = selectedGroupId === g.id;
                                    return (
                                        <button
                                            key={g.id}
                                            onClick={() => setSelectedGroupId(g.id)}
                                            className={`relative flex items-center gap-3 rounded-xl border-2 p-4 transition-all text-left ${
                                                isSelected ? 'border-emerald-500 bg-emerald-50/30 shadow-sm' : 'border-gray-100 hover:border-gray-300'
                                            }`}
                                        >
                                            {isSelected && <div className="absolute top-2.5 right-2.5 h-3 w-3 rounded-full bg-emerald-500" />}
                                            <div className="min-w-0">
                                                <p className={`text-sm font-semibold truncate ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>{g.name}</p>
                                                {g.description && <p className="text-xs text-gray-400 truncate mt-0.5">{g.description}</p>}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Download Template */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="flex items-center gap-2 mb-4">
                            <Download className="h-5 w-5 text-gray-400" />
                            <h2 className="text-sm font-semibold text-gray-900">2. Baixe o Modelo</h2>
                        </div>
                        <p className="text-xs text-gray-500 mb-4">Baixe um template com os campos necessários incluindo steps, preencha com seus flows e faça o upload.</p>
                        <div className="flex gap-3">
                            <Button variant="outline" className="gap-2 rounded-xl" onClick={downloadJsonTemplate}>
                                <FileJson className="h-4 w-4 text-amber-600" /> Template JSON (com Steps)
                            </Button>
                            <Button variant="outline" className="gap-2 rounded-xl" onClick={downloadCsvTemplate}>
                                <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Template CSV (sem Steps)
                            </Button>
                        </div>
                        <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 p-4">
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Campos do flow</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {TEMPLATE_FIELDS.filter(f => f.key !== 'steps').map(f => (
                                    <div key={f.key} className="flex items-center gap-1.5 text-xs">
                                        {f.required ? (
                                            <span className="h-1.5 w-1.5 rounded-full bg-red-400 shrink-0" />
                                        ) : (
                                            <span className="h-1.5 w-1.5 rounded-full bg-gray-300 shrink-0" />
                                        )}
                                        <span className="font-mono text-gray-600">{f.key}</span>
                                        {f.required && <span className="text-red-400 text-[10px]">*</span>}
                                    </div>
                                ))}
                            </div>
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2 mt-4">Tipos de step (JSON)</p>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="flex items-center gap-1.5 text-xs">
                                    <Bell className="h-3 w-3 text-blue-500" />
                                    <span className="font-mono text-gray-600">notification</span>
                                    <span className="text-gray-400 text-[10px]">title, body, url</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs">
                                    <Timer className="h-3 w-3 text-amber-500" />
                                    <span className="font-mono text-gray-600">delay</span>
                                    <span className="text-gray-400 text-[10px]">delay_minutes</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Upload */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="flex items-center gap-2 mb-4">
                            <Upload className="h-5 w-5 text-gray-400" />
                            <h2 className="text-sm font-semibold text-gray-900">3. Faça o Upload</h2>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".json,.csv"
                            className="hidden"
                            onChange={handleFileUpload}
                        />
                        <button
                            onClick={() => (selectedDomainId || selectedGroupId) ? fileInputRef.current?.click() : toast({ variant: 'destructive', title: 'Selecione um domínio ou grupo primeiro' })}
                            className={`w-full flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 transition-all ${
                                (selectedDomainId || selectedGroupId)
                                    ? 'border-emerald-300 bg-emerald-50/30 hover:border-emerald-400 hover:bg-emerald-50/50 cursor-pointer'
                                    : 'border-gray-200 bg-gray-50/30 cursor-not-allowed opacity-60'
                            }`}
                        >
                            <Upload className={`h-8 w-8 ${(selectedDomainId || selectedGroupId) ? 'text-emerald-400' : 'text-gray-300'}`} />
                            <p className="mt-3 text-sm font-medium text-gray-700">
                                Arraste ou clique para selecionar
                            </p>
                            <p className="mt-1 text-xs text-gray-400">Aceita .json ou .csv</p>
                        </button>
                    </div>
                </div>
            )}

            {step === 'preview' && (
                <div className="space-y-6">
                    {/* Summary */}
                    <div className="flex items-center gap-4">
                        <Button variant="outline" size="sm" className="gap-1.5 rounded-xl" onClick={() => { setStep('upload'); setItems([]); }}>
                            <ArrowLeft className="h-4 w-4" /> Voltar ao Upload
                        </Button>
                        <div className="flex-1" />
                        <div className="flex items-center gap-3 text-sm">
                            <span className="flex items-center gap-1.5 text-emerald-600 font-medium">
                                <Check className="h-4 w-4" /> {validItems.length} válidos
                            </span>
                            {invalidItems.length > 0 && (
                                <span className="flex items-center gap-1.5 text-red-500 font-medium">
                                    <AlertTriangle className="h-4 w-4" /> {invalidItems.length} com erros
                                </span>
                            )}
                            {totalStepsCount > 0 && (
                                <span className="text-gray-500 text-xs">
                                    ({totalStepsCount} step{totalStepsCount !== 1 ? 's' : ''} total)
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Invalid Items Warning */}
                    {invalidItems.length > 0 && (
                        <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="h-4 w-4 text-red-500" />
                                <p className="text-sm font-semibold text-red-700">{invalidItems.length} flows com erros</p>
                            </div>
                            <p className="text-xs text-red-600">Estes flows não serão criados. Corrija no arquivo e faça upload novamente, ou remova-os da lista.</p>
                        </div>
                    )}

                    {/* Flow List */}
                    <div className="space-y-3">
                        {items.map((item, idx) => {
                            const isExpanded = expandedIdx === idx;
                            const triggerLabel = item.trigger_type === 'new_lead' ? 'Novo Lead' : `Tag: ${item.trigger_tag}`;
                            const stepsCount = item.steps?.length || 0;
                            return (
                                <div
                                    key={idx}
                                    className={`rounded-2xl border bg-white shadow-sm transition-all ${
                                        item._valid ? 'border-gray-100' : 'border-red-200 bg-red-50/30'
                                    }`}
                                >
                                    {/* Flow header */}
                                    <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setExpandedIdx(isExpanded ? null : idx)}>
                                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                                            item._valid ? 'bg-emerald-100' : 'bg-red-100'
                                        }`}>
                                            {item._valid ? (
                                                <Workflow className="h-5 w-5 text-emerald-600" />
                                            ) : (
                                                <AlertTriangle className="h-5 w-5 text-red-500" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-gray-900 truncate">{item.name || 'Sem nome'}</p>
                                                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                                                    {item.trigger_type === 'new_lead' ? <UserPlus className="h-3 w-3" /> : <Tag className="h-3 w-3" />}
                                                    {triggerLabel}
                                                </span>
                                                {stepsCount > 0 && (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600">
                                                        {stepsCount} step{stepsCount !== 1 ? 's' : ''}
                                                    </span>
                                                )}
                                            </div>
                                            {item.description && <p className="text-xs text-gray-500 truncate mt-0.5">{item.description}</p>}
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <button onClick={(e) => { e.stopPropagation(); removeItem(idx); }} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                            <div className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400">
                                                {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Errors */}
                                    {!item._valid && item._errors && (
                                        <div className="px-4 pb-3">
                                            <div className="flex flex-wrap gap-1.5">
                                                {item._errors.map((err, ei) => (
                                                    <span key={ei} className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-[11px] font-medium text-red-700">
                                                        <X className="h-3 w-3" /> {err}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Expanded: Flow fields + Steps editaveis */}
                                    {isExpanded && (
                                        <div className="border-t border-gray-100 px-4 py-4 space-y-4">
                                            {/* Editable flow fields */}
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Nome *</label>
                                                    <input
                                                        className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none"
                                                        value={item.name || ''}
                                                        onChange={(e) => updateFlowField(idx, 'name', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Descrição</label>
                                                    <input
                                                        className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none"
                                                        value={item.description || ''}
                                                        onChange={(e) => updateFlowField(idx, 'description', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Trigger *</label>
                                                    <select
                                                        className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none"
                                                        value={item.trigger_type || 'new_lead'}
                                                        onChange={(e) => updateFlowField(idx, 'trigger_type', e.target.value)}
                                                    >
                                                        <option value="new_lead">Novo Lead</option>
                                                        <option value="tag_added">Tag Adicionada</option>
                                                    </select>
                                                </div>
                                                {item.trigger_type === 'tag_added' && (
                                                    <div>
                                                        <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Tag do Trigger *</label>
                                                        <input
                                                            className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none"
                                                            value={item.trigger_tag || ''}
                                                            onChange={(e) => updateFlowField(idx, 'trigger_tag', e.target.value)}
                                                        />
                                                    </div>
                                                )}
                                                <div>
                                                    <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">UTM Source</label>
                                                    <input
                                                        className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none"
                                                        value={item.utm_source || ''}
                                                        onChange={(e) => updateFlowField(idx, 'utm_source', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">UTM Medium</label>
                                                    <input
                                                        className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none"
                                                        value={item.utm_medium || ''}
                                                        onChange={(e) => updateFlowField(idx, 'utm_medium', e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            {/* Steps */}
                                            <div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                                        Steps ({item.steps?.length || 0})
                                                    </p>
                                                    <div className="flex gap-1.5">
                                                        <button
                                                            onClick={() => addStep(idx, 'notification')}
                                                            className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2 py-1 text-[11px] font-medium text-blue-600 hover:bg-blue-100 transition-colors"
                                                        >
                                                            <Plus className="h-3 w-3" /> Notification
                                                        </button>
                                                        <button
                                                            onClick={() => addStep(idx, 'delay')}
                                                            className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-600 hover:bg-amber-100 transition-colors"
                                                        >
                                                            <Plus className="h-3 w-3" /> Delay
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="space-y-2">
                                                    {(item.steps || []).map((s, si) => {
                                                        const isEditing = editingStep?.flowIdx === idx && editingStep?.stepIdx === si;
                                                        return (
                                                            <div key={si} className={`rounded-xl border ${getStepColor(s.type)} overflow-hidden`}>
                                                                {/* Step header row */}
                                                                <div className="flex items-center gap-2.5 px-3 py-2">
                                                                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-white/80 shrink-0">
                                                                        {getStepIcon(s.type)}
                                                                    </span>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-xs font-medium text-gray-800 truncate">
                                                                            {getStepSummary(s)}
                                                                        </p>
                                                                        {s.type === 'notification' && s.body && !isEditing && (
                                                                            <p className="text-[11px] text-gray-500 truncate">{s.body}</p>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex items-center gap-1 shrink-0">
                                                                        <span className="text-[10px] text-gray-400 mr-1">#{si + 1}</span>
                                                                        <button
                                                                            onClick={() => setEditingStep(isEditing ? null : { flowIdx: idx, stepIdx: si })}
                                                                            className="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 hover:text-blue-600 hover:bg-white/80 transition-colors"
                                                                        >
                                                                            <Pencil className="h-3 w-3" />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => removeStep(idx, si)}
                                                                            className="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-white/80 transition-colors"
                                                                        >
                                                                            <Trash2 className="h-3 w-3" />
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {/* Step edit form */}
                                                                {isEditing && (
                                                                    <div className="border-t border-white/50 bg-white/40 px-3 py-3">
                                                                        {s.type === 'notification' && (
                                                                            <div className="grid grid-cols-1 gap-2">
                                                                                <div>
                                                                                    <label className="text-[10px] font-medium text-gray-500">Title *</label>
                                                                                    <input
                                                                                        className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-800 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
                                                                                        value={s.title || ''}
                                                                                        onChange={(e) => updateStep(idx, si, 'title', e.target.value)}
                                                                                        placeholder="Titulo da notificação"
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <label className="text-[10px] font-medium text-gray-500">Body *</label>
                                                                                    <input
                                                                                        className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-800 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
                                                                                        value={s.body || ''}
                                                                                        onChange={(e) => updateStep(idx, si, 'body', e.target.value)}
                                                                                        placeholder="Corpo da notificação"
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <label className="text-[10px] font-medium text-gray-500">URL *</label>
                                                                                    <input
                                                                                        className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-800 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
                                                                                        value={s.url || ''}
                                                                                        onChange={(e) => updateStep(idx, si, 'url', e.target.value)}
                                                                                        placeholder="https://..."
                                                                                    />
                                                                                </div>
                                                                                <div className="grid grid-cols-2 gap-2">
                                                                                    <div>
                                                                                        <label className="text-[10px] font-medium text-gray-500">Ícone</label>
                                                                                        {s.icon ? (
                                                                                            <div className="mt-0.5 flex items-center gap-2">
                                                                                                <img src={s.icon} alt="" className="h-8 w-8 rounded-md object-cover border border-gray-200" />
                                                                                                <button
                                                                                                    onClick={() => setMediaPicker({ flowIdx: idx, stepIdx: si, field: 'icon' })}
                                                                                                    className="text-[10px] text-blue-600 hover:underline"
                                                                                                >Alterar</button>
                                                                                                <button
                                                                                                    onClick={() => updateStep(idx, si, 'icon', '')}
                                                                                                    className="text-[10px] text-red-500 hover:underline"
                                                                                                >Remover</button>
                                                                                            </div>
                                                                                        ) : (
                                                                                            <button
                                                                                                onClick={() => setMediaPicker({ flowIdx: idx, stepIdx: si, field: 'icon' })}
                                                                                                className="mt-0.5 w-full flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 bg-white px-2.5 py-2 text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                                                                                            >
                                                                                                <ImagePlus className="h-3.5 w-3.5" /> Escolher ícone
                                                                                            </button>
                                                                                        )}
                                                                                    </div>
                                                                                    <div>
                                                                                        <label className="text-[10px] font-medium text-gray-500">Imagem</label>
                                                                                        {s.image ? (
                                                                                            <div className="mt-0.5 flex items-center gap-2">
                                                                                                <img src={s.image} alt="" className="h-8 w-14 rounded-md object-cover border border-gray-200" />
                                                                                                <button
                                                                                                    onClick={() => setMediaPicker({ flowIdx: idx, stepIdx: si, field: 'image' })}
                                                                                                    className="text-[10px] text-blue-600 hover:underline"
                                                                                                >Alterar</button>
                                                                                                <button
                                                                                                    onClick={() => updateStep(idx, si, 'image', '')}
                                                                                                    className="text-[10px] text-red-500 hover:underline"
                                                                                                >Remover</button>
                                                                                            </div>
                                                                                        ) : (
                                                                                            <button
                                                                                                onClick={() => setMediaPicker({ flowIdx: idx, stepIdx: si, field: 'image' })}
                                                                                                className="mt-0.5 w-full flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 bg-white px-2.5 py-2 text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                                                                                            >
                                                                                                <ImagePlus className="h-3.5 w-3.5" /> Escolher imagem
                                                                                            </button>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="grid grid-cols-2 gap-2">
                                                                                    <div>
                                                                                        <label className="text-[10px] font-medium text-gray-500">UTM Campaign</label>
                                                                                        <input
                                                                                            className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-800 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
                                                                                            value={s.utm_campaign || ''}
                                                                                            onChange={(e) => updateStep(idx, si, 'utm_campaign', e.target.value)}
                                                                                        />
                                                                                    </div>
                                                                                    <div>
                                                                                        <label className="text-[10px] font-medium text-gray-500">Tag on Click</label>
                                                                                        <input
                                                                                            className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-800 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
                                                                                            value={s.tag_on_click || ''}
                                                                                            onChange={(e) => updateStep(idx, si, 'tag_on_click', e.target.value)}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {s.type === 'delay' && (
                                                                            <div>
                                                                                <label className="text-[10px] font-medium text-gray-500">Delay (minutos) *</label>
                                                                                <div className="flex gap-2 mt-1">
                                                                                    {[5, 15, 30, 60].map(m => (
                                                                                        <button
                                                                                            key={m}
                                                                                            onClick={() => updateStep(idx, si, 'delay_minutes', m)}
                                                                                            className={`rounded-lg px-2.5 py-1 text-[11px] font-medium transition-colors ${
                                                                                                s.delay_minutes === m
                                                                                                    ? 'bg-amber-200 text-amber-800'
                                                                                                    : 'bg-white/80 text-gray-600 hover:bg-amber-100'
                                                                                            }`}
                                                                                        >
                                                                                            {m < 60 ? `${m}min` : `${m / 60}h`}
                                                                                        </button>
                                                                                    ))}
                                                                                    <input
                                                                                        type="number"
                                                                                        min={1}
                                                                                        className="w-20 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-800 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                                                                                        value={s.delay_minutes || ''}
                                                                                        onChange={(e) => updateStep(idx, si, 'delay_minutes', parseInt(e.target.value) || 0)}
                                                                                        placeholder="min"
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {(!item.steps || item.steps.length === 0) && (
                                                    <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center">
                                                        <p className="text-xs text-gray-400">Nenhum step adicionado. Use os botões acima para adicionar.</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Submit */}
                    {items.length > 0 && (
                        <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                            <div>
                                {progress ? (
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900">
                                            Criando flow {progress.flow}/{progress.totalFlows}
                                            {progress.totalSteps > 0 && ` — step ${progress.step}/${progress.totalSteps}`}
                                        </p>
                                        <div className="mt-1.5 h-1.5 w-48 rounded-full bg-gray-100 overflow-hidden">
                                            <div
                                                className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                                                style={{
                                                    width: `${((progress.flow - 1 + (progress.totalSteps > 0 ? progress.step / progress.totalSteps : 1)) / progress.totalFlows) * 100}%`,
                                                }}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <p className="text-sm font-semibold text-gray-900">
                                            {validItems.length} flow{validItems.length !== 1 ? 's' : ''} pronto{validItems.length !== 1 ? 's' : ''} para criar
                                            {totalStepsCount > 0 && ` (${totalStepsCount} step${totalStepsCount !== 1 ? 's' : ''})`}
                                        </p>
                                        {invalidItems.length > 0 && (
                                            <p className="text-xs text-gray-500 mt-0.5">{invalidItems.length} serão ignorados por conter erros</p>
                                        )}
                                    </>
                                )}
                            </div>
                            <Button
                                className="gap-2 bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-sm"
                                disabled={validItems.length === 0 || creating}
                                onClick={handleSubmit}
                            >
                                {creating ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Zap className="h-4 w-4" />
                                )}
                                Criar {validItems.length} Flow{validItems.length !== 1 ? 's' : ''}
                            </Button>
                        </div>
                    )}
                </div>
            )}
            {/* Media Picker Modal */}
            {mediaPicker && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setMediaPicker(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[75vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-4 border-b">
                            <h3 className="text-sm font-semibold text-gray-900">
                                {mediaPicker.field === 'icon' ? 'Escolher Ícone' : 'Escolher Imagem'}
                            </h3>
                            <button onClick={() => setMediaPicker(null)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                        </div>

                        {/* Upload area */}
                        <div className="px-4 pt-4">
                            <label className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-5 cursor-pointer transition-all ${uploading ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'}`}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    disabled={uploading}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleMediaUpload(file);
                                        e.target.value = '';
                                    }}
                                />
                                {uploading ? (
                                    <>
                                        <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
                                        <p className="mt-2 text-xs text-blue-600 font-medium">Enviando...</p>
                                    </>
                                ) : (
                                    <>
                                        <Upload className="h-6 w-6 text-gray-400" />
                                        <p className="mt-2 text-xs text-gray-600 font-medium">Fazer upload</p>
                                        <p className="text-[11px] text-gray-400">JPG, PNG, GIF, WebP, SVG</p>
                                    </>
                                )}
                            </label>
                        </div>

                        {/* Biblioteca */}
                        <div className="px-4 pt-3 pb-1">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Biblioteca</p>
                        </div>
                        <div className="flex-1 overflow-y-auto px-4 pb-4">
                            <div className="grid grid-cols-4 gap-2.5">
                                {(mediaPicker.field === 'icon'
                                    ? mediaList.filter(m => m.media_type === 'icon')
                                    : mediaList.filter(m => m.media_type === 'image')
                                ).map((media) => (
                                    <button
                                        key={media.id}
                                        onClick={() => {
                                            updateStep(mediaPicker.flowIdx, mediaPicker.stepIdx, mediaPicker.field, getMediaUrl(media.filepath));
                                            setMediaPicker(null);
                                        }}
                                        className="group relative rounded-lg border-2 border-transparent hover:border-gray-900 overflow-hidden transition-all"
                                    >
                                        <img src={getThumbnailUrl(media)} alt={media.original_filename} className="w-full aspect-square object-cover" />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                                            <Check className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                    </button>
                                ))}
                                {mediaList.filter(m => mediaPicker.field === 'icon' ? m.media_type === 'icon' : m.media_type === 'image').length === 0 && (
                                    <div className="col-span-4 py-6 text-center text-sm text-gray-400">
                                        Nenhuma mídia encontrada. Faça upload acima.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
